/* Plan Access Config — Frontend
 *
 * MIRRORS backend/src/config/planAccess.js. This copy only controls what the
 * UI shows/hides and where it redirects — it is NOT the security boundary
 * (the backend enforces the real rule on every API call, since a user could
 * always bypass the UI). If you change a tier's access, update BOTH files.
 *
 * Why not fetch this from the backend instead of duplicating it? Could do
 * later, but for a handful of tools it's simpler to keep two small files in
 * sync than add another API round-trip on every page load.
 */

export const TOOL_IDS = {
  DRAFT_BOARD:        'draft-board',
  WAIVER_WIRE:        'waiver-wire',
  TRADE_ANALYSER:     'trade-analyser',
  PLAYER_ANALYSER:    'player-analyser',
  TEAM_ANALYSER:      'team-analyser',
  OPPONENT_SCOUT:     'opponent-scout',
  NOTES:              'notes',
  SCHEDULE_SURGEON:   'schedule-surgeon',
  INJURY_INSURANCE:   'injury-insurance',
  GAME_NIGHT:         'game-night',
  NEWSPAPER:          'newspaper',
  CHAMPIONSHIP_ODDS:  'championship-odds',
  BENCH_REGRET:       'bench-regret',
  FANTASY_DNA:        'fantasy-dna',
  GRUDGE_MATCH:       'grudge-match',
  POWER_RANKINGS:     'power-rankings',
  TRASH_TALK:         'trash-talk',
  CRYSTAL_BALL:       'crystal-ball',
  TEAM_NEWS:          'team-news',
  LEAGUE_NEWS:        'league-news',
}

const LAUNCH_VISIBLE_TOOLS = [
  TOOL_IDS.DRAFT_BOARD,
  TOOL_IDS.WAIVER_WIRE,
  TOOL_IDS.TRADE_ANALYSER,
  TOOL_IDS.PLAYER_ANALYSER,
  TOOL_IDS.TEAM_ANALYSER,
  TOOL_IDS.OPPONENT_SCOUT,
  TOOL_IDS.NOTES,
  TOOL_IDS.SCHEDULE_SURGEON,
  TOOL_IDS.INJURY_INSURANCE,
]

const ALL_TOOLS = Object.values(TOOL_IDS)

export const PLAN_TOOL_ACCESS = {
  starter: [TOOL_IDS.DRAFT_BOARD, TOOL_IDS.WAIVER_WIRE, TOOL_IDS.TRADE_ANALYSER],
  pro:     LAUNCH_VISIBLE_TOOLS,
  elite:   ALL_TOOLS,
}

// Max notes per league. MIRRORS NOTES_PER_LEAGUE_LIMIT in
// backend/src/config/planAccess.js — display only (the composer's "N / 30"
// counter before the first API round trip); the backend enforces the real
// cap and returns it in the 409 body, so this constant is never the only
// thing standing between a user and a 31st note.
export const NOTES_PER_LEAGUE_LIMIT = 30

// How many leagues a user on each plan can connect. `null` = unlimited.
// Elite was unlimited (null) until 2026-08-12 — capped at 6 as part of the
// founding-pricing launch (see backend/src/config/planAccess.js for why).
// MIRRORS PLAN_LEAGUE_LIMIT in backend/src/config/planAccess.js — display
// only (used by the "choose which league(s) to keep" screen); the backend
// enforces the real limit.
export const PLAN_LEAGUE_LIMIT = {
  starter: 1,
  pro:     3,
  elite:   6,
}

// Starter's Draft Board gives full analysis for rounds 1-5, then an upgrade
// prompt from round 6 on. `null` = every round gets full analysis. MIRRORS
// PLAN_DRAFT_ROUND_LIMIT in backend/src/config/planAccess.js.
export const PLAN_DRAFT_ROUND_LIMIT = {
  starter: 5,
  pro:     null,
  elite:   null,
}

// How many strategies JAI offers in Strategy Establishment. MIRRORS
// PLAN_STRATEGY_COUNT in backend/src/config/planAccess.js — the backend
// truncates the actual list returned; this only sizes the UI.
export const PLAN_STRATEGY_COUNT = { starter: 2, pro: 3, elite: 5 }

// Whether the user can keep refining a strategy via the custom-prompt box.
// `null` = unlimited turns. MIRRORS PLAN_STRATEGY_CUSTOM_TURNS.
export const PLAN_STRATEGY_CUSTOM_TURNS = { starter: 1, pro: null, elite: null }

// Whether Live Draft reads the real platform feed, or falls back to the
// manual "Already Selected" flow. MIRRORS PLAN_LIVE_DRAFT_FEED.
export const PLAN_LIVE_DRAFT_FEED = { starter: false, pro: true, elite: true }

// JAI credits a plan gets per rolling 30 days. MIRRORS PLAN_CREDIT_MONTHLY_LIMIT
// in backend/src/config/planAccess.js — display only; the backend enforces the
// real limit in actionQuotaMiddleware.js and charges in claudeService.js.
// 1 credit = 3c of real Claude cost; a quick question uses 1, a draft strategy
// build ~20. Also the numbers ADVERTISED in config/plans.js. Change one, change
// all three (backend config, this file, plans.js).
//
// Prefer the live figure from GET /api/billing/status (`credits.limit`) wherever
// the real balance is shown — a 7-day trial gets 25% of this, which only the
// server knows. This constant is for static copy only.
export const PLAN_CREDIT_MONTHLY_LIMIT = { starter: 30, pro: 250, elite: 500 }
export const CREDIT_USD = 0.03

// Credit top-ups, sold on play-jai.com only (never in the iOS app). MIRRORS
// CREDIT_TOPUP_PACKS in the backend; the server owns the real price.
export const CREDIT_TOPUP_PACKS = [
  { id: 'topup_100', credits: 100, priceCents: 599 },
  { id: 'topup_300', credits: 300, priceCents: 1499 },
]

/**
 * @deprecated Superseded by PLAN_CREDIT_MONTHLY_LIMIT. Signed-in users are no
 * longer metered in requests. Kept for one release; nothing reads it.
 */
export const PLAN_ACTION_MONTHLY_LIMIT = { starter: 100, pro: 400, elite: 750 }

// @deprecated Superseded by PLAN_CREDIT_MONTHLY_LIMIT. The hourly chat cap is
// no longer enforced anywhere (every backend entry is null) and the daily cap
// is no longer mounted on a route. Kept for one release so nothing breaks.

export const planIncludesTool = (plan, toolId) =>
  (PLAN_TOOL_ACCESS[plan] || PLAN_TOOL_ACCESS.starter).includes(toolId)

// True if this plan should bypass the pre-launch HIDDEN_TOOL_PATHS block —
// i.e. Elite gets "first access" to a tool the moment it's built, even
// before it's rolled out to everyone else during the campaign.
export const planBypassesLaunchScope = (plan) => plan === 'elite'
